import React, { useMemo, useState } from "react";
import DOMPurify from "dompurify";
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CBadge,
  CListGroup,
  CListGroupItem,
  CButtonGroup,
} from "@coreui/react";

/** Utils */
const isObj = (x) => x && typeof x === "object" && !Array.isArray(x);
const isArray = Array.isArray;
const isScalar = (v) =>
  typeof v === "string" ||
  typeof v === "number" ||
  typeof v === "boolean" ||
  v === null;

const prettyJSON = (v) => JSON.stringify(v, null, 2);
const isHtmlString = (s) =>
  typeof s === "string" && /<\/?[a-z][\s\S]*>/i.test(s.trim()); // heurística leve

// word-diff leve (LCS)
function diffWords(aStr = "", bStr = "") {
  const a = String(aStr).split(/\s+/);
  const b = String(bStr).split(/\s+/);
  const n = a.length,
    m = b.length;
  const dp = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--)
      dp[i][j] =
        a[i] === b[j]
          ? 1 + dp[i + 1][j + 1]
          : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const chunks = [];
  let i = 0,
    j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      chunks.push({ type: "eq", text: a[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      chunks.push({ type: "del", text: a[i++] });
    } else {
      chunks.push({ type: "add", text: b[j++] });
    }
  }
  while (i < n) chunks.push({ type: "del", text: a[i++] });
  while (j < m) chunks.push({ type: "add", text: b[j++] });
  return chunks;
}

function InlineDiff({ before, after }) {
  const parts = useMemo(
    () => diffWords(before ?? "", after ?? ""),
    [before, after]
  );
  return (
    <div style={{ lineHeight: 1.45, wordBreak: "break-word" }}>
      {parts.map((p, idx) => {
        if (p.type === "eq") return <span key={idx}>{p.text} </span>;
        if (p.type === "del")
          return (
            <mark key={idx} style={{ background: "#ffe1e1" }}>
              {p.text}{" "}
            </mark>
          );
        return (
          <mark key={idx} style={{ background: "#d9f9d9" }}>
            {p.text}{" "}
          </mark>
        );
      })}
    </div>
  );
}

// Caixa colapsável com limites visuais
function CollapsibleBox({
  bg,
  children,
  opened,
  onToggle,
  maxHeight = 140,
  label,
  style,
  padded = true,
}) {
  return (
    <div
      className="mb-2"
      style={{
        background: bg,
        borderRadius: 8,
        position: "relative",
        maxWidth: "100%",
        ...style,
      }}
    >
      {label && (
        <div className="px-2 py-1" style={{ fontSize: 12, opacity: 0.8 }}>
          {label}
        </div>
      )}
      <div
        style={{
          padding: padded ? "8px 12px" : 0,
          maxHeight: opened ? "none" : maxHeight,
          overflow: "hidden",
        }}
      >
        {children}
      </div>
      {!opened && (
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 40,
            background:
              "linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0.85))",
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
          }}
        />
      )}
      <div className="px-2 pb-2" style={{ position: "relative", zIndex: 1 }}>
        <CButton size="sm" color="link" className="px-0" onClick={onToggle}>
          {opened ? "Fechar" : "Expandir"}
        </CButton>
      </div>
    </div>
  );
}

/**
 * DiffModalList
 *  - base: valores atuais (servidor)
 *  - changed: { fieldId: valorDoDraftCompleto }
 */
export default function DiffModal({ visible, onClose, base, changed }) {
  const fields = useMemo(() => Object.keys(changed || {}), [changed]);
  const [modes, setModes] = useState({}); // { field: 'diff' | 'atual' | 'rascunho' }
  const [expanded, setExpanded] = useState({}); // { field: boolean }

  const setMode = (field, mode) =>
    setModes((prev) => ({ ...prev, [field]: mode }));
  const toggleExpanded = (field) =>
    setExpanded((p) => ({ ...p, [field]: !p[field] }));

  const renderScalar = (v) => {
    if (v === null) return <em className="text-muted">null</em>;
    if (typeof v === "boolean" || typeof v === "number")
      return <code>{String(v)}</code>;
    // string
    if (isHtmlString(v)) {
      const html = DOMPurify.sanitize(v);
      return (
        <div
          style={{ wordBreak: "break-word" }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    }
    return <span style={{ wordBreak: "break-word" }}>{v}</span>;
  };

  const renderJSON = (v) => (
    <pre
      className="mb-0"
      style={{
        background: "transparent",
        fontSize: 12,
        whiteSpace: "pre",
        overflowX: "auto",
        maxWidth: "100%",
      }}
    >
      {prettyJSON(v)}
    </pre>
  );

  // layout helper: 2 colunas 50/50 fixo quando “ambos”
  const TwoCols = ({ left, right }) => (
    <div
      className="d-flex gap-2"
      style={{ width: "100%", maxWidth: "100%", alignItems: "stretch" }}
    >
      <div style={{ flex: "1 1 0%", minWidth: 0, maxWidth: "50%" }}>{left}</div>
      <div style={{ flex: "1 1 0%", minWidth: 0, maxWidth: "50%" }}>
        {right}
      </div>
    </div>
  );

  return (
    <CModal visible={visible} onClose={onClose} size="xl" scrollable>
      <CModalHeader>
        <CModalTitle>
          Alterações por campo
          <CBadge color="warning" className="ms-2">
            {fields.length} campo(s)
          </CBadge>
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        {fields.length === 0 ? (
          <small className="text-muted">Sem diferenças para mostrar.</small>
        ) : (
          <CListGroup className="mb-3">
            {fields.map((field) => {
              const before = base?.[field];
              const after = changed?.[field];
              const mode = modes[field] || "diff";
              const open = !!expanded[field];

              let content = null;

              if (mode === "diff") {
                // Strings com HTML → mostrar ambos renderizados lado-a-lado (50/50)
                if (
                  typeof before === "string" &&
                  typeof after === "string" &&
                  (isHtmlString(before) || isHtmlString(after))
                ) {
                  content = (
                    <TwoCols
                      left={
                        <CollapsibleBox
                          bg="#fff5f5"
                          opened={open}
                          onToggle={() => toggleExpanded(field)}
                          label="Original (HTML)"
                        >
                          {renderScalar(before)}
                        </CollapsibleBox>
                      }
                      right={
                        <CollapsibleBox
                          bg="#f2fff2"
                          opened={open}
                          onToggle={() => toggleExpanded(field)}
                          label="Rascunho (HTML)"
                        >
                          {renderScalar(after)}
                        </CollapsibleBox>
                      }
                    />
                  );
                } else if (
                  typeof before === "string" &&
                  typeof after === "string"
                ) {
                  // Texto simples → inline diff
                  content = (
                    <CollapsibleBox
                      bg="#fff"
                      opened={open}
                      onToggle={() => toggleExpanded(field)}
                      style={{ maxWidth: "100%" }}
                    >
                      <InlineDiff before={before} after={after} />
                    </CollapsibleBox>
                  );
                } else if (isScalar(before) && isScalar(after)) {
                  // Primitivos → ambos (50/50)
                  content = (
                    <TwoCols
                      left={
                        <CollapsibleBox
                          bg="#fff5f5"
                          opened={open}
                          onToggle={() => toggleExpanded(field)}
                          label="Original"
                        >
                          {renderScalar(before)}
                        </CollapsibleBox>
                      }
                      right={
                        <CollapsibleBox
                          bg="#f2fff2"
                          opened={open}
                          onToggle={() => toggleExpanded(field)}
                          label="Rascunho"
                        >
                          {renderScalar(after)}
                        </CollapsibleBox>
                      }
                    />
                  );
                } else {
                  // Arrays/Objetos → JSON lado-a-lado (50/50) com scroll horizontal local
                  content = (
                    <TwoCols
                      left={
                        <CollapsibleBox
                          bg="#fff5f5"
                          opened={open}
                          onToggle={() => toggleExpanded(field)}
                          label="Original (JSON)"
                        >
                          {renderJSON(before)}
                        </CollapsibleBox>
                      }
                      right={
                        <CollapsibleBox
                          bg="#f2fff2"
                          opened={open}
                          onToggle={() => toggleExpanded(field)}
                          label="Rascunho (JSON)"
                        >
                          {renderJSON(after)}
                        </CollapsibleBox>
                      }
                    />
                  );
                }
              } else if (mode === "atual") {
                // Vista do valor atual (servidor)
                content = (
                  <CollapsibleBox
                    bg={isScalar(before) ? "#fff5f5" : "#fff5f5"}
                    opened={open}
                    onToggle={() => toggleExpanded(field)}
                    style={{ maxWidth: "100%" }}
                  >
                    {isScalar(before)
                      ? renderScalar(before)
                      : renderJSON(before)}
                  </CollapsibleBox>
                );
              } else {
                // "rascunho"
                content = (
                  <CollapsibleBox
                    bg={isScalar(after) ? "#f2fff2" : "#f2fff2"}
                    opened={open}
                    onToggle={() => toggleExpanded(field)}
                    style={{ maxWidth: "100%" }}
                  >
                    {isScalar(after) ? renderScalar(after) : renderJSON(after)}
                  </CollapsibleBox>
                );
              }

              return (
                <CListGroupItem
                  key={field}
                  className="py-3"
                  style={{ maxWidth: "100%" }}
                >
                  <div
                    className="d-flex justify-content-between align-items-start flex-wrap gap-2"
                    style={{ maxWidth: "100%" }}
                  >
                    <div
                      className="fw-semibold"
                      style={{ wordBreak: "break-word" }}
                    >
                      {field}
                    </div>
                    <CButtonGroup
                      size="sm"
                      role="group"
                      aria-label="Modo de visualização"
                    >
                      <CButton
                        color={
                          (modes[field] || "diff") === "diff"
                            ? "warning"
                            : "secondary"
                        }
                        variant={
                          (modes[field] || "diff") === "diff"
                            ? "outline"
                            : "ghost"
                        }
                        onClick={() => setMode(field, "diff")}
                      >
                        Diferenças
                      </CButton>
                      <CButton
                        color={
                          (modes[field] || "diff") === "atual"
                            ? "danger"
                            : "secondary"
                        }
                        variant={
                          (modes[field] || "diff") === "atual"
                            ? "outline"
                            : "ghost"
                        }
                        onClick={() => setMode(field, "atual")}
                      >
                        Atual
                      </CButton>
                      <CButton
                        color={
                          (modes[field] || "diff") === "rascunho"
                            ? "success"
                            : "secondary"
                        }
                        variant={
                          (modes[field] || "diff") === "rascunho"
                            ? "outline"
                            : "ghost"
                        }
                        onClick={() => setMode(field, "rascunho")}
                      >
                        Rascunho
                      </CButton>
                    </CButtonGroup>
                  </div>

                  <div className="mt-2" style={{ maxWidth: "100%" }}>
                    {content}
                  </div>
                </CListGroupItem>
              );
            })}
          </CListGroup>
        )}
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" onClick={onClose}>
          Fechar
        </CButton>
      </CModalFooter>
    </CModal>
  );
}
